<template>
    <v-dialog
        v-model="dialogModel"
        width="768"
        max-width="95%"
        @click:outside.prevent="closeDialog"
    >
        <v-card>
            <v-card-title class="text-h6 text-md-h5 grey lighten-2">
                <v-row>
                    <v-col cols="10">
                        Bank Details (Accounts)
                    </v-col>
                    <v-col cols="2" class="text-right">
                        <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text class="py-5" style="max-height: 75vh; overflow-y: auto;">
                <v-form ref="bankDetailsForm" lazy-validation>
                    <h2 class="mb-2">
                        Please type or use capital letters when completing this form.
                    </h2>
                    <p class="body-2 mb-4">Your employer needs the information on this form before your first payday to tell HMRC about you and help them use the correct tax code. Do not send this form to HMRC.</p>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Company Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.companyName"
                                placeholder="Company Name"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="align-end mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Full Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.firstName"
                                placeholder="First Name"
                                outlined
                                dense
                                hint="First Name"
                                :persistent-hint="true"
                                :rules="firstNameRules"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="form.lastName"
                                placeholder="Last Name"
                                outlined
                                dense
                                hint="Last Name"
                                :persistent-hint="true"
                                :rules="lastNameRules"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">National Insurance Number <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nationalInsuranceNumber"
                                placeholder="Insurance Number"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Passport Number</h3>
                            <v-text-field
                                v-model="form.passportNumber"
                                placeholder="Passport Number"
                                outlined
                                dense
                                max-length="20"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Bank Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.bankName"
                                placeholder="Bank Name"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Name on Account <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nameOnAccount"
                                placeholder="Name on Account"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Account Number <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.accountNumber"
                                placeholder="8 Digit Code"
                                outlined
                                dense
                                maxlength="8"
                                :rules="accountNumberRules"
                                hint="8 Digit Code"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Sort Code <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.sortCode"
                                placeholder="6 Digit Code"
                                outlined
                                dense
                                maxlength="6"
                                :rules="sortCodeRules"
                                hint="6 Digit Code"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Building Society Roll Number</h3>
                            <v-text-field
                                v-model="form.buildingSocietyRollNumber"
                                placeholder="Building Society Roll Number"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>

                    <div class="mt-5 pt-5" style="border-top:1px solid #e0e0e0">
                        <div class="d-flex justify-end">
                            <v-btn color="primary" :loading="submitLoading" @click="onSubmit">
                                Submit
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import { mapActions } from 'vuex'
    import validationRules from '@/common/validationRules'

    const emptyForm = () => ({
        companyName: '',
        firstName: '',
        lastName: '',
        nationalInsuranceNumber: '',
        passportNumber: '',
        bankName: '',
        nameOnAccount: '',
        accountNumber: '',
        sortCode: '',
        buildingSocietyRollNumber: '',
        formType: 'BankDetails'
    })

    export default {
        name: 'BankDetailFormDialog',
        props: ['isOpen', 'programme'],

        data: () => ({
            form: emptyForm(),
            submitLoading: false,
            firstNameRules: validationRules.firstName,
            lastNameRules: validationRules.lastName,
            requiredRules: validationRules.required,
            // todo: remove if not needed
            // nationalInsuranceRules: [
            //     validationRules.required,
            //     value => /^[A-CEGHJ-PR-TW-Z]{2}\d{6}[A-D]$/i.test(String(value || '').trim()) || 'National Insurance Number must be valid (e.g. QQ123456C)'
            // ],
            accountNumberRules: [
                validationRules.required,
                value => /^\d{8}$/.test(String(value || '').trim()) || 'Account Number must be 8 digits'
            ],
            sortCodeRules: [
                validationRules.required,
                value => /^\d{6}$/.test(String(value || '').trim()) || 'Sort Code must be 6 digits'
            ]
        }),
        computed: {
            dialogModel: {
                get () {
                    return this.isOpen
                },
                set (value) {
                    this.$emit('update:isOpen', value)
                }
            }
        },

        methods: {
            ...mapActions([
                'uploadBankDetailsForm', 'showAlert'
            ]),
            buildPayload () {
                return {
                    transitionProgrammeCourseId: this.programme && this.programme.id ? this.programme.id : 0,
                    companyName: this.form.companyName,
                    firstName: this.form.firstName,
                    lastName: this.form.lastName,
                    nationalInsuranceNumber: this.form.nationalInsuranceNumber,
                    passportNumber: this.form.passportNumber,
                    bankName: this.form.bankName,
                    nameOnAccount: this.form.nameOnAccount,
                    accountNumber: this.form.accountNumber,
                    sortCode: this.form.sortCode,
                    buildingSocietyRollNumber: this.form.buildingSocietyRollNumber,
                    formType: this.form.formType
                }
            },
            resetForm () {
                this.form = emptyForm()
            },
            closeDialog () {
                this.resetForm()
                this.$emit('update:isOpen', false)
            },
            async onSubmit () {
                const formRef = this.$refs.bankDetailsForm
                if (!formRef.validate()) {
                    this.showAlert({
                        text: 'Fill all the required fields please',
                        type: 'error'
                    })
                    return
                }

                this.submitLoading = true
                try {
                    const payload = this.buildPayload()
                    await this.uploadBankDetailsForm(payload)
                    this.$emit('saved')
                    this.closeDialog()
                    if (formRef && formRef.resetValidation) formRef.resetValidation()
                } catch (e) {
                    this.showAlert({
                        text: e.message || 'Error saving form',
                        type: 'error'
                    })
                } finally {
                    this.submitLoading = false
                }
            }
        }
    }
</script>
